import { useState, useCallback, useEffect } from 'react';

export const useBookingData = (totalPassengers) => {
  const [bookingData, setBookingData] = useState(() => {
    return {
      contactInfo: {
        data: null,
        hasErrors: false,
      },
      passengers: Array(totalPassengers || 0)
        .fill(null)
        .map(() => ({
          personalInfo: null,
          emergencyContact: null,
          hasErrors: false,
        })),
      fiscalData: {
        useCommonData: false,
        commonDataPassengerIndex: null,
        hasErrors: false,
        data: Array(totalPassengers || 0).fill({
          documentType: '',
          documentNumber: '',
          cuitCuil: '',
        }),
      },
    };
  });

  useEffect(() => {
    if (totalPassengers > 0 && bookingData.passengers.length !== totalPassengers) {
      setBookingData((prev) => {
        const passengers = Array(totalPassengers)
          .fill(null)
          .map((_, i) =>
            i < prev.passengers.length
              ? prev.passengers[i]
              : {
                  personalInfo: null,
                  emergencyContact: null,
                  hasErrors: false,
                }
          );

        const fiscalData = {
          ...prev.fiscalData,
          data: Array(totalPassengers)
            .fill(null)
            .map((_, i) =>
              i < prev.fiscalData.data.length
                ? prev.fiscalData.data[i]
                : { documentType: '', documentNumber: '', cuitCuil: '' }
            ),
        };

        return {
          ...prev,
          passengers,
          fiscalData,
        };
      });
    }
  }, [totalPassengers]);

  const updateContactInfo = useCallback((data, hasErrors = false) => {
    if (!data) {
      console.error('Intentando actualizar contactInfo con datos vacíos');
      return;
    }

    setBookingData((prev) => {
      const updated = {
        ...prev,
        contactInfo: {
          data,
          hasErrors,
        },
      };
      return updated;
    });
  }, []);

  const updatePassengerInfo = useCallback((index, data, hasErrors = false) => {
    if (!data) {
      console.error(`Intentando actualizar pasajero ${index} con datos vacíos`);
      return;
    }

    setBookingData((prev) => {
      const passengers = [...prev.passengers];
      while (passengers.length <= index) {
        passengers.push({
          personalInfo: null,
          emergencyContact: null,
          hasErrors: false,
        });
      }

      passengers[index] = {
        ...passengers[index],
        personalInfo: data,
        hasErrors,
      };

      return {
        ...prev,
        passengers,
      };
    });
  }, []);

  const updateEmergencyContact = useCallback((index, data, hasErrors = false) => {
    if (index < 0 || index === undefined) {
      console.error('Índice de pasajero inválido para actualizar contacto de emergencia');
      return;
    }

    setBookingData((prev) => {
      const passengers = [...prev.passengers];
      while (passengers.length <= index) {
        passengers.push({
          personalInfo: null,
          emergencyContact: null,
          hasErrors: false,
        });
      }

      passengers[index] = {
        ...passengers[index],
        emergencyContact: data,
        hasErrors: passengers[index].hasErrors || hasErrors,
      };

      return {
        ...prev,
        passengers,
      };
    });
  }, []);

  const updateFiscalData = useCallback(
    (index, data, isCommonData = false, hasErrors = false) => {
      if (!data) {
        console.error('Intentando actualizar datos fiscales con datos vacíos');
        return;
      }

      setBookingData((prev) => {
        const fiscalDataArray = [...prev.fiscalData.data];
        while (fiscalDataArray.length < totalPassengers) {
          fiscalDataArray.push({
            documentType: '',
            documentNumber: '',
            cuitCuil: '',
          });
        }

        if (isCommonData) {
          return {
            ...prev,
            fiscalData: {
              useCommonData: true,
              commonDataPassengerIndex: index,
              hasErrors,
              data: Array(totalPassengers).fill(data),
            },
          };
        } else {
          if (prev.fiscalData.useCommonData) {
            fiscalDataArray[index] = data;

            return {
              ...prev,
              fiscalData: {
                useCommonData: false,
                commonDataPassengerIndex: null,
                hasErrors: prev.fiscalData.hasErrors || hasErrors,
                data: fiscalDataArray,
              },
            };
          }

          fiscalDataArray[index] = data;

          return {
            ...prev,
            fiscalData: {
              ...prev.fiscalData,
              hasErrors: prev.fiscalData.hasErrors || hasErrors,
              data: fiscalDataArray,
            },
          };
        }
      });
    },
    [totalPassengers]
  );

  const hasErrors = useCallback(() => {
    return (
      bookingData.contactInfo.hasErrors ||
      bookingData.passengers.some((p) => p?.hasErrors) ||
      bookingData.fiscalData.hasErrors
    );
  }, [bookingData]);

  const isComplete = useCallback(() => {
    const isContactComplete = !!bookingData.contactInfo.data && !bookingData.contactInfo.hasErrors;

    const arePassengersComplete =
      bookingData.passengers.length === totalPassengers &&
      bookingData.passengers.every((p) => !!p?.personalInfo && !p?.hasErrors);

    const isFiscalDataComplete =
      bookingData.fiscalData.data.length === totalPassengers &&
      bookingData.fiscalData.data.every((d) => d?.documentType && d?.documentNumber) &&
      !bookingData.fiscalData.hasErrors;

    return isContactComplete && arePassengersComplete && isFiscalDataComplete;
  }, [bookingData, totalPassengers]);

  return {
    bookingData,
    updateContactInfo,
    updatePassengerInfo,
    updateEmergencyContact,
    updateFiscalData,
    hasErrors,
    isComplete,
  };
};
